/**
 * MicMonitor — owns the microphone's RETURN path: the branch that sends a live
 * microphone back to the speakers so a performer can hear themselves.
 *
 * WHY THIS IS A PRIMITIVE AND NOT EIGHT LINES INSIDE EACH TOOL
 *
 * MicManager already owns getting the microphone IN — permissions, recovery
 * modals, device lists, context healing. Nothing owned the way back OUT, and
 * that is the half that can hurt: a monitor return is the one audio path a tool
 * can build that feeds its own input. Three tools built it independently and
 * each answered the safety question differently:
 *
 *   /record            pill + level slider + headphones warning, default OFF
 *   /vocal             checkbox, persisted, default ON at 0.7
 *   /recording-studio  forced ON at 0.7 — NO control of any kind
 *
 * The third is what this file exists for. Its monitor path is a line-for-line
 * copy of /vocal's, down to the 0.7 and the variable names, but the eight lines
 * of checkbox + persistence that make it switchable were left behind. The
 * `var monitorOn = true;  // hear yourself while recording (toggle)` comment
 * promised a toggle that was never built, so the answer to a user asking how to
 * turn it off was: you cannot.
 *
 * MEASURED, 30 days, RU shard, before this landed:
 *   - 1836 sessions started a recording; every one of them heard itself
 *   - 117 sessions clipped EVERY take they made — the signature of a monitor
 *     going to speakers with echoCancellation:false and the backing track live
 *   - those sessions exported at 11.1% against a 19.2% baseline (-42%), while
 *     sessions that clipped only SOME takes exported at 36.3%, which is what
 *     rules out "clipping just means they tried harder"
 *   - the toast they got said "reduce your microphone volume", which is the
 *     wrong knob: the level came back through our monitor, not their gain
 *
 * DEFAULT IS OFF, DELIBERATELY.
 * Monitoring only helps through headphones and only hurts through speakers, and
 * the two cases are not symmetric: OFF costs a headphone user one visible click,
 * while ON costs a speaker user their take with no control to reach for. We
 * looked for a way to stop guessing and tell the two apart automatically —
 * enumerateDevices() does expose "Default - Headphones (Realtek(R) Audio)" on
 * Windows once mic permission is granted — but the label is whatever the driver
 * and the OS locale say, and a Bluetooth headset just reports its model name.
 * That is an open-ended string-matching surface that fails silently and
 * differently on every machine, so the default is a stated policy, not a guess.
 *
 * The preference is shared across recording tools rather than per-tool: whether
 * you are wearing headphones is a fact about you, not about the page.
 *
 * USAGE — two steps, and the first one matters as much as the second.
 *
 *   // 1. At page load, so the switch can be FOUND before pressing record.
 *   //    Call it from DOMContentLoaded: a tool's inline script has no `defer`
 *   //    and parses before this file loads, so an inline call sees no
 *   //    MicMonitor and silently mounts nothing.
 *   MicMonitor.control({
 *       tool: 'recording-studio',
 *       mount: el.someSlot,
 *       hintMount: el.somePanel,       // optional; defaults to mount's parent
 *       labels: {label, hint, on, off}
 *   });
 *
 *   // 2. When the microphone opens. Adopts the control from step 1 rather
 *   //    than rendering a second one, and gives it something to switch.
 *   var mon = MicMonitor.attach(ctx, {
 *       tool:   'recording-studio',
 *       source: micSource,             // AudioNode to monitor
 *       fx:     {input: .., output: ..} // optional wet chain, monitor-only
 *       mount:  el.someSlot,           // fallback if step 1 never ran
 *       labels: {label, hint, on, off}
 *   });
 *   mon.enabled            // -> boolean
 *   mon.setEnabled(true)
 *   mon.detach()           // drops the audio; an ADOPTED control stays on the
 *                          // page, because it belongs to the page, not the take
 */
(function () {
'use strict';

var STORE_TOOL = 'mic-monitor';      // shared namespace — preference follows the user
var STORE_KEY  = 'on';
var LEGACY_KEY = 'timbrica_monitor'; // /vocal's own key from before this primitive
var DEFAULT_ON = false;
var DEFAULT_LEVEL = 0.7;

var TEXT = {
    en: { label: 'Hear myself', hint: 'Use headphones — through speakers this feeds back into the recording.', on: 'on', off: 'off' },
    ru: { label: 'Слышать себя', hint: 'Наденьте наушники — через динамики это попадёт обратно в запись.', on: 'вкл', off: 'выкл' }
};

function textFor(locale) {
    return TEXT[locale] || TEXT[(locale || '').slice(0, 2)] || TEXT.en;
}

/* An explicit choice wins wherever it was made — including the key /vocal wrote
   before this primitive existed, so nobody who already turned monitoring on
   loses it to the new default. */
function loadPref() {
    try {
        if (window.__toolSettings) {
            var v = window.__toolSettings.load(STORE_TOOL, STORE_KEY, null);
            if (v !== null && v !== undefined) return !!v;
        }
    } catch (e) {}
    try {
        var legacy = localStorage.getItem(LEGACY_KEY);
        if (legacy !== null) return legacy !== 'false';
    } catch (e) {}
    return DEFAULT_ON;
}

function savePref(on) {
    try { if (window.__toolSettings) window.__toolSettings.save(STORE_TOOL, STORE_KEY, !!on); } catch (e) {}
    try { localStorage.setItem(LEGACY_KEY, on ? 'true' : 'false'); } catch (e) {}
}

var stylesDone = false;
function ensureStyles() {
    if (stylesDone) return;
    stylesDone = true;
    var s = document.createElement('style');
    s.textContent = [
        '.mic-mon{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border:1px solid var(--border,rgba(255,255,255,.14));',
        'border-radius:var(--radius-md,8px);background:var(--glass,rgba(255,255,255,.05));color:var(--text-secondary,#b9c0cc);',
        'font-size:13px;cursor:pointer;line-height:1.2;transition:background .15s,border-color .15s,color .15s}',
        '.mic-mon:hover{background:var(--glass-hover,rgba(255,255,255,.09));border-color:var(--border-hover,rgba(255,255,255,.24))}',
        '.mic-mon[aria-pressed="true"]{color:var(--accent,#4f8cff);border-color:var(--accent,#4f8cff)}',
        '.mic-mon svg{width:15px;height:15px;flex:none}',
        '.mic-mon-state{opacity:.7;font-size:12px}',
        '.mic-mon-hint{display:flex;align-items:center;gap:6px;margin:6px 0 0;padding:6px 10px;font-size:12px;line-height:1.35;',
        'border-radius:var(--radius-sm,6px);background:rgba(245,158,11,.10);border:1px solid rgba(245,158,11,.30);color:var(--warning,#f59e0b)}',
        '.mic-mon-hint[hidden]{display:none}'
    ].join('');
    document.head.appendChild(s);
}

var ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<path d="M3 18v-6a9 9 0 0 1 18 0v6"/>' +
    '<path d="M3 18a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3v-3a3 3 0 0 0-3-3H3M21 18a3 3 0 0 1-3 3h0a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h3"/></svg>';

/* Controls rendered before any audio exists, keyed by tool. A singer looking
   for "how do I hear myself" must find the switch BEFORE pressing record —
   mounting it only from initMic() hides it until they are already recording,
   which is the same discoverability failure as having no switch at all, just
   one screen later. */
var pendingControls = {};

function MicMonitor(ctx, opts) {
    opts = opts || {};
    this.ctx = ctx;
    this.tool = opts.tool || 'unknown';
    this.level = typeof opts.level === 'number' ? opts.level : DEFAULT_LEVEL;
    this.labels = Object.assign({}, textFor(window.__LOCALE || 'en'), opts.labels || {});
    this._enabled = loadPref();
    this._fx = opts.fx || null;
    this._source = opts.source || null;
    /* Страница узнаёт о НАЖАТИИ человека. До 19.09.2026 такого канала не было,
       и переключатель, нажатый ДО записи, был чистой декларацией: `ctx === null`,
       значит `gain === null`, звукового пути нет — а кнопка вставала в «вкл» и
       поднимала предупреждение про наушники, то есть заявляла живое и даже
       опасное состояние. Заявка 18.09.2026 (/recording-studio, Yandex 26.8):
       «включила функцию слышать себя не работает». */
    this._onUserToggle = opts.onUserToggle || null;

    /* ctx === null builds a CONTROL ONLY: the switch exists on the page before
       any microphone does, so it can be found before pressing record. */
    this.gain = ctx ? ctx.createGain() : null;
    if (this.gain) this.gain.gain.value = this._enabled ? this.level : 0;

    if (this.gain && this._source) {
        if (this._fx && this._fx.input && this._fx.output) {
            this._source.connect(this._fx.input);
            this._fx.output.connect(this.gain);
        } else {
            this._source.connect(this.gain);
        }
    }
    if (this.gain) this.gain.connect(ctx.destination);

    /* Adopt a control already on the page rather than rendering a second one. */
    var pre = pendingControls[this.tool];
    if (pre && pre._btn && pre._btn.parentNode) {
        this._btn = pre._btn;
        this._hint = pre._hint;
        this._adopted = true;
        pre._owner = this;
        this._syncUI();
    } else if (opts.mount) {
        this.mount(opts.mount, opts.hintMount);
    }
}

/* Renders the control. A tool with its own place for it may skip this and drive
   setEnabled() itself — but it must expose SOMETHING, which is what the guard
   test shipping alongside this file enforces. */
MicMonitor.prototype.mount = function (container, hintContainer) {
    if (!container) return;
    ensureStyles();
    var self = this;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mic-mon';
    btn.id = 'micMonToggle';
    btn.setAttribute('aria-pressed', this._enabled ? 'true' : 'false');
    btn.innerHTML = ICON + '<span>' + this.labels.label + '</span><span class="mic-mon-state"></span>';
    /* Route to whichever instance currently owns the audio. Before recording
       that is this control itself (preference only); once initMic() attaches,
       the live instance adopts these same nodes and must be the one that moves
       the gain — otherwise the button keeps working and the sound does not. */
    btn.addEventListener('click', function () {
        var target = (pendingControls[self.tool] && pendingControls[self.tool]._owner) || self;
        target.setEnabled(!target._enabled, { user: true });
    });

    var hint = document.createElement('div');
    hint.className = 'mic-mon-hint';
    hint.id = 'micMonHint';
    hint.hidden = !this._enabled;
    hint.innerHTML = '<span aria-hidden="true">🎧</span><span>' + this.labels.hint + '</span>';

    this._btn = btn;
    this._hint = hint;
    container.appendChild(btn);
    (hintContainer || container.parentNode || container).appendChild(hint);
    this._syncUI();
};

MicMonitor.prototype._syncUI = function () {
    if (this._btn) {
        this._btn.setAttribute('aria-pressed', this._enabled ? 'true' : 'false');
        var st = this._btn.querySelector('.mic-mon-state');
        /* «вкл» имеет право писать только тот, у кого ЕСТЬ звуковой путь.
           Владелец без `gain` — это контрол до открытия микрофона; он говорит
           «ждёт», а не «вкл», иначе человек слышит тишину при надписи «вкл». */
        var owner = (pendingControls[this.tool] && pendingControls[this.tool]._owner) || this;
        var live = !!owner.gain;
        if (st) st.textContent = this._enabled ? (live ? this.labels.on : (this.labels.pending || this.labels.on)) : this.labels.off;
    }
    /* The headphones warning belongs to the DANGEROUS state only. Shown always,
       it is wallpaper by the time it matters. */
    /* Подсказку НЕ привязываем к «живости» пути: у /vocal и /record переключатель
       до записи по-прежнему только объявляет выбор, и предупреждение,
       показанное заранее, там полезно — а менять их поведение этой правкой
       незачем. В студии путь теперь включается сразу, поэтому подсказка и так
       совпадает с реальностью. */
    if (this._hint) this._hint.hidden = !this._enabled;
};

MicMonitor.prototype.setEnabled = function (on, opts) {
    on = !!on;
    var changed = on !== this._enabled;
    this._enabled = on;
    if (this.gain) {
        /* Ramp — a step on a live monitor path is an audible click in the
           performer's ears, and they are wearing headphones on our own advice. */
        var g = this.gain.gain, now = this.ctx.currentTime;
        try {
            g.cancelScheduledValues(now);
            g.setValueAtTime(g.value, now);
            g.linearRampToValueAtTime(on ? this.level : 0, now + 0.02);
        } catch (e) { g.value = on ? this.level : 0; }
    }
    this._syncUI();
    if (changed) savePref(on);
    if (changed && opts && opts.user) {
        try {
            if (window.__ae) window.__ae('feature_use', { slug: this.tool, f: 'mic_monitor_toggle', on: on ? 1 : 0 });
        } catch (e) {}
        /* Обработчик берём у ЗАРЕГИСТРИРОВАННОГО контрола (его ставит страница
           в control() на загрузке), а не только у себя: живой экземпляр
           создаётся позже, внутри initMic, и своего обработчика не имеет. */
        var pre = pendingControls[this.tool];
        var cb = this._onUserToggle || (pre && pre._onUserToggle) || null;
        if (cb) { try { cb(on, this); } catch (e) {} }
    }
    return this;
};

Object.defineProperty(MicMonitor.prototype, 'enabled', {
    get: function () { return this._enabled; },
    set: function (v) { this.setEnabled(v); }
});

MicMonitor.prototype.detach = function () {
    try { if (this._source) this._source.disconnect(this._fx && this._fx.input ? this._fx.input : this.gain); } catch (e) {}
    try { if (this._fx && this._fx.output) this._fx.output.disconnect(this.gain); } catch (e) {}
    try { if (this.gain) this.gain.disconnect(); } catch (e) {}
    this.gain = null;
    this._source = null;
    this._fx = null;
    /* A control this instance ADOPTED belongs to the page, not to the take —
       tearing it down at stopMic() would make the switch vanish the moment
       recording ends, which is how it became unfindable in the first place. */
    if (this._adopted) {
        var pre = pendingControls[this.tool];
        if (pre && pre._owner === this) { pre._owner = null; pre._enabled = this._enabled; pre._syncUI(); }
    } else {
        if (this._btn && this._btn.parentNode) this._btn.parentNode.removeChild(this._btn);
        if (this._hint && this._hint.parentNode) this._hint.parentNode.removeChild(this._hint);
    }
    this._btn = this._hint = null;
};

MicMonitor.attach = function (ctx, opts) { return new MicMonitor(ctx, opts); };

/* Render the switch at page load, before any AudioContext exists. attach()
   later adopts these nodes instead of rendering a second copy. */
MicMonitor.control = function (opts) {
    opts = opts || {};
    var tool = opts.tool || 'unknown';
    var existing = pendingControls[tool];
    if (existing && existing._btn && existing._btn.parentNode) return existing;
    /* mount:null for the constructor — it would render one control, and the
       explicit mount() below would render a SECOND with the same element ids.
       Two #micMonToggle / #micMonHint in the DOM means getElementById returns
       one of them while the live instance drives the other: the button works,
       the headphones warning does not. Caught by the contract test, not by eye. */
    var stub = new MicMonitor(null, Object.assign({}, opts, { source: null, fx: null, mount: null }));
    stub._onUserToggle = opts.onUserToggle || null;
    pendingControls[tool] = stub;
    if (opts.mount) stub.mount(opts.mount, opts.hintMount);
    return stub;
};
MicMonitor.defaultOn = DEFAULT_ON;
MicMonitor.loadPref = loadPref;

window.MicMonitor = MicMonitor;
})();
